import {
  Radio,
  FormControlLabel,
  Typography,
  Box,
  Paper,
  Button,
  Collapse,
  Divider
} from '@mui/material'
import React, { useEffect, useState, useRef } from 'react'
import { ApplePayLogo } from '../../../../components/logos'
import { useDispatch, useSelector } from 'react-redux'
import { b2cSignupSlice } from '../../../../reducers'
import { useRecurly } from '@recurly/react-recurly'
import usePaymentEvent from '../usePaymentEvent'

const MERCHANT_COUNTRY_CODE = 'US'

export const ApplePay = ({ selectedValue }) => {
  const checkoutPricing = useSelector(
    b2cSignupSlice.selectors.selectCheckoutPricing
  )
  const recurly = useRecurly()
  const [applePayReady, setApplePayReady] = useState(false)
  const currency = useSelector(b2cSignupSlice.selectors.selectCurrency)
  const account = useSelector(b2cSignupSlice.selectors.selectAccount)
  const coupon = useSelector(b2cSignupSlice.selectors.selectCoupon)
  const paymentInfoSubmitted = useSelector(b2cSignupSlice.selectors.selectPaymentInfoSubmitted)
  const buttonRef = useRef(null)

  const initiatePayment = usePaymentEvent({
    onComplete: () => {
      dispatch(b2cSignupSlice.actions.setPaymentInfoSubmitted(false))
    },
    onError: () => {
      dispatch(b2cSignupSlice.actions.setPaymentInfoSubmitted(false))
    }
  })

  const plan = useSelector(b2cSignupSlice.selectors.selectPlan)
  const dispatch = useDispatch()
  const [applePay, setApplePay] = useState(null)

  useEffect(() => {
    const missingAccount = !account?.username || !account?.firstName || !account?.lastName || !account?.email
    const missingPricing = applePayReady || !recurly || !checkoutPricing?.now?.items.length || !currency
    if (missingAccount || missingPricing) return

    const applePay = recurly.ApplePay({
      country: MERCHANT_COUNTRY_CODE,
      currency,
      label: 'Mindtools',
      total: checkoutPricing?.now?.total
    })
    setApplePay(applePay)

    applePay.on('ready', () => {
      setApplePayReady(true)
    })

    applePay.on('error', (e) => {
      dispatch(b2cSignupSlice.actions.setPaymentInfoSubmitted(false))
    })

    applePay.on('paymentAuthorized', ({ payment }) => {
      const { recurlyToken: token } = payment
      const payload = {
        planCode: plan?.code,
        currency,
        username: account.username,
        firstName: account.firstName,
        lastName: account.lastName,
        email: account.email,
        tokenId: token.id,
        paymentChannel: 'applepay',
        couponCodes: coupon
      }
      initiatePayment.trigger(payload)
    })
  }, [applePay, checkoutPricing, currency, recurly, applePayReady, plan, account, coupon, dispatch, initiatePayment])

  const handleClick = () => {
    applePay.begin()
    dispatch(b2cSignupSlice.actions.setPaymentInfoSubmitted(true))
  }

  return (
    <Paper variant='standard' sx={{ mb: 1, p: 2, width: '100%', borderRadius: '10px', border: '1px solid black' }}>
      <FormControlLabel
        value='applePay'
        control={<Radio />}
        label={
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            width='100%'
          >
            <Typography sx={{ flexGrow: 1 }} variant='h6'>
              Apple Pay
            </Typography>
            <ApplePayLogo style={{ position: 'absolute', right: '24px' }} />
          </Box>
            }
      />
      <Collapse in={selectedValue === 'applePay'}>

        {/* Plan Details */}
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography fontSize={14} fontWeight='bold' color='black'>
              Current plan
            </Typography>
            <Typography fontSize={14} sx={{ mt: 1 }} color='black'>
              {plan?.name}
            </Typography>

          </Box>
          <Typography fontSize={14} fontWeight='bold'>{checkoutPricing?.currency?.symbol} {checkoutPricing?.next?.total}</Typography>
        </Box>

        <Divider sx={{ my: 1 }} color='black' />

        {/* Order Total */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography fontWeight='bold'>Order Total</Typography>
          <Typography fontWeight='bold' fontSize={18}>{checkoutPricing?.currency?.symbol} {checkoutPricing?.now?.total}</Typography>
        </Box>

        {/* Checkout Button */}
        <Button
          fullWidth
          ref={buttonRef}
          disabled={!applePayReady || paymentInfoSubmitted}
          variant='contained'
          onClick={handleClick}
          sx={{
            mt: 2,
            py: 1.5,
            backgroundColor: '#000',
            color: '#fff',
            fontWeight: 'bold',
            fontSize: 16,
            borderRadius: 2,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#111'
            }
          }}
          endIcon={<ApplePayLogo style={{ fill: 'white' }} />}
        >
          Checkout with
        </Button>

        {/* Terms */}
        <Typography
          fontSize={11}
          color='black'
          sx={{ mt: 2 }}
        >
          By continuing, you allow Mindtools to charge your Apple Pay for this payment and future payments in accordance with their terms.
        </Typography>

      </Collapse>
    </Paper>
  )
}
